// client/src/services/postalCodeService.js
// Postnummer-uppslag mot stad/region. Statisk fallback-tabell i fil +
// API-lookup mot diseaseReports som lär sig nya postnummer över tid.
import API_CONFIG from '../config/apiConfig';

// Statisk uppslagstabell för vanliga svenska postnummer (fallback)
const postalCodeDatabase = {
  // Stockholm (10xxx - 18xxx)
  '11434': 'Stockholm',
  '11867': 'Stockholm',
  '10044': 'Stockholm',
  '11520': 'Stockholm',
  '12345': 'Stockholm',
  // Göteborg (40xxx - 42xxx)
  '41118': 'Göteborg', 
  '41701': 'Göteborg',
  // Malmö (21xxx - 22xxx)
  '21119': 'Malmö',
  '21140': 'Malmö',
  '21237': 'Malmö',
  // Övriga städer
  '75236': 'Uppsala',
  '72215': 'Umeå',
  '58218': 'Linköping',
  '70210': 'Örebro',
  '55318': 'Jönköping',
  '60220': 'Norrköping',
  '25221': 'Helsingborg',
  '50468': 'Borås',
  '63220': 'Eskilstuna',
  '80267': 'Gävle',
  '85235': 'Sundsvall',
  '65224': 'Karlstad',
  '35233': 'Växjö',
  '39235': 'Kalmar',
  '97128': 'Luleå',
  '79131': 'Falun',
  '46152': 'Trollhättan'
};

// ✅ Lokal cache för API-resultat (persistas i sessionStorage)
const CACHE_KEY = 'postalCodeCache';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 timmar

/**
 * Hämta cache från sessionStorage
 */
const getCache = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Rensa gamla entries
      const now = Date.now();
      const valid = {};
      Object.entries(parsed).forEach(([key, value]) => {
        if (value.timestamp && (now - value.timestamp) < CACHE_EXPIRY) {
          valid[key] = value;
        }
      });
      return valid;
    }
  } catch (e) {
    console.warn('Cache read error:', e);
  }
  return {};
};

/**
 * Spara till cache
 */
const setCache = (postalCode, city) => {
  try {
    const cache = getCache();
    cache[postalCode] = {
      city,
      timestamp: Date.now()
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    
    // Lägg också till i runtime-databasen
    postalCodeDatabase[postalCode] = city;
    
    console.log(`📮 Cached: ${postalCode} → ${city}`);
  } catch (e) {
    console.warn('Cache write error:', e);
  }
};

/**
 * Hämta stad baserat på postnummer
 * Kollar: 1) Statisk lista 2) Cache 3) API
 * @param {string} postalCode - Postnummer att söka efter
 * @returns {string|null} - Staden eller null
 */
export const getCityByPostalCode = (postalCode) => {
  if (!postalCode) return null;
  
  const normalized = postalCode.replace(/\s+/g, '');
  
  // 1. Kolla statisk lista först
  if (postalCodeDatabase[normalized]) {
    return postalCodeDatabase[normalized];
  }
  
  // 2. Kolla cache
  const cache = getCache();
  if (cache[normalized]?.city) {
    return cache[normalized].city;
  }
  
  // 3. Returnera null - async lookup görs separat
  return null;
};

/**
 * ✅ NY: Async lookup från API
 * @param {string} postalCode - Postnummer att söka efter
 * @returns {Promise<string|null>} - Staden eller null
 */
export const lookupCityByPostalCode = async (postalCode) => {
  if (!postalCode) return null;
  
  const normalized = postalCode.replace(/\s+/g, '');
  
  // Kolla lokalt först
  const localResult = getCityByPostalCode(normalized);
  if (localResult) {
    return localResult;
  }
  
  // Anropa API
  try {
    console.log(`📮 API lookup for: ${normalized}`);
    const response = await fetch(`${API_CONFIG.baseUrl}/disease-reports/postal-lookup/${normalized}`);
    const result = await response.json();
    
    if (result.success && result.data?.city) {
      // Cache resultatet
      setCache(normalized, result.data.city);
      return result.data.city;
    }
  } catch (error) {
    console.warn('Postal lookup API error:', error);
  }
  
  return null;
};

/**
 * Sök efter postnummer baserat på prefix
 * Kombinerar statisk lista + API-förslag
 * @param {string} prefix - Början på postnummer
 * @param {number} limit - Max antal resultat
 * @returns {Array} - Array med matchande postnummer och städer
 */
export const searchPostalCodes = (prefix, limit = 5) => {
  if (!prefix || prefix.length < 2) return [];
  
  const normalized = prefix.replace(/\s+/g, '');
  const results = [];
  const seen = new Set();
  
  // 1. Sök i statisk lista + cache
  const cache = getCache();
  const allData = { ...postalCodeDatabase };
  
  // Lägg till cached entries
  Object.entries(cache).forEach(([code, data]) => {
    if (data.city) {
      allData[code] = data.city;
    }
  });
  
  Object.entries(allData).forEach(([code, city]) => {
    if (code.startsWith(normalized) && results.length < limit && !seen.has(code)) {
      results.push({ code, city });
      seen.add(code);
    }
  });
  
  return results;
};

/**
 * ✅ NY: Async sök med API-förslag
 * @param {string} prefix - Början på postnummer
 * @param {number} limit - Max antal resultat
 * @returns {Promise<Array>} - Array med matchande postnummer och städer
 */
export const searchPostalCodesAsync = async (prefix, limit = 10) => {
  if (!prefix || prefix.length < 2) return [];
  
  const normalized = prefix.replace(/\s+/g, '');
  
  // Hämta lokala först
  const localResults = searchPostalCodes(normalized, limit);
  
  // Om vi har tillräckligt, returnera direkt
  if (localResults.length >= limit) {
    return localResults;
  }
  
  // Annars, hämta från API
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}/disease-reports/postal-suggestions?prefix=${normalized}`);
    const result = await response.json();
    
    if (result.success && result.data) {
      const seen = new Set(localResults.map(r => r.code));
      
      result.data.forEach(item => {
        if (!seen.has(item.code) && localResults.length < limit) {
          localResults.push({
            code: item.code,
            city: item.city
          });
          seen.add(item.code);
          
          // Cache för framtida användning
          setCache(item.code, item.city);
        }
      });
    }
  } catch (error) {
    console.warn('Postal suggestions API error:', error);
  }
  
  return localResults;
};

/**
 * ✅ NY: Lägg till postnummer-mappning (sparar i cache + skickas till server via rapport)
 * @param {string} postalCode - Postnumret
 * @param {string} city - Staden
 * @returns {boolean} - True om det var ett nytt postnummer
 */
export const addPostalCode = (postalCode, city) => {
  if (!postalCode || !city || city === 'Unknown City' || city.trim() === '') {
    return false;
  }
  
  const normalized = postalCode.replace(/\s+/g, '');
  
  // Kolla om vi redan har det
  if (postalCodeDatabase[normalized] === city) {
    return false; // Redan känt
  }
  
  // Spara lokalt
  setCache(normalized, city);
  postalCodeDatabase[normalized] = city;
  
  console.log(`🎯 System learned: ${normalized} → ${city}`);
  return true;
};

/**
 * Validera postnummer (svenskt format)
 * @param {string} postalCode - Postnumret att validera
 * @returns {boolean} - Sant om postnumret är giltigt
 */
export const validatePostalCode = (postalCode) => {
  if (!postalCode) return false;
  const normalized = postalCode.replace(/\s+/g, '');
  return /^\d{5}$/.test(normalized);
};

/**
 * Formatera postnummer med mellanslag (från 12345 till 123 45)
 * @param {string} postalCode - Postnumret att formatera
 * @returns {string} - Formaterat postnummer
 */
export const formatPostalCode = (postalCode) => {
  if (!postalCode) return '';
  const normalized = postalCode.replace(/\s+/g, '');
  
  if (normalized.length === 5) {
    return `${normalized.substring(0, 3)} ${normalized.substring(3)}`;
  }
  
  return normalized;
};

/**
 * Normalisera postnummer (ta bort mellanslag)
 * @param {string} postalCode - Postnumret att normalisera
 * @returns {string} - Normaliserat postnummer
 */
export const normalizePostalCode = (postalCode) => {
  if (!postalCode) return '';
  return postalCode.replace(/\s+/g, '');
};

// Exportera alla funktioner som ett objekt också
export default {
  getCityByPostalCode,
  lookupCityByPostalCode,  // ✅ NY
  searchPostalCodes,
  searchPostalCodesAsync,   // ✅ NY
  addPostalCode,            // ✅ ÅTERSKAPAD
  validatePostalCode,
  formatPostalCode,
  normalizePostalCode
};
